import { z } from 'zod';
import {
  AdvanceFilter,
  AdvanceFilterConfig,
  AdvanceFilterFieldType,
  AdvanceFilterOperator,
  FilterTypeOperatorMap,
} from '@/types/types';

/** OpenAPI `DdiWorkOrderGetAllRequest.filters[].field` enum (excluding null). */
export const DDI_FILTER_FIELDS = [
  'ddiType',
  'ddiSubtype',
  'state',
  'caseType',
  'caseSubType',
  'contactName',
  'fileNumber',
  'lawyer',
  'caseName',
  'caseNumber',
  'clientName',
  'address',
  'city',
  'zipCode',
  'courtName',
  'personForInquiry',
  'entityForInquiry',
  'vin',
  'assignedUser',
  'clientCode',
  'workOrderNumber',
  'status',
  'createdAt',
  'dueDate',
] as const;

export type DdiFilterField = (typeof DDI_FILTER_FIELDS)[number];

/** Default list-view search fields → API filter fieldType/operator (matches backend applyFilter). */
export const DDI_DEFAULT_FILTER_FIELD_CONFIG: Record<
  string,
  { fieldType: AdvanceFilterFieldType; operator: AdvanceFilterOperator }
> = {
  workOrderNumber: { fieldType: AdvanceFilterFieldType.ID, operator: AdvanceFilterOperator.EQUALS },
  clientCode: { fieldType: AdvanceFilterFieldType.ID, operator: AdvanceFilterOperator.EQUALS },
  status: { fieldType: AdvanceFilterFieldType.ID, operator: AdvanceFilterOperator.EQUALS },
  caseNumber: { fieldType: AdvanceFilterFieldType.STRING, operator: AdvanceFilterOperator.CONTAINS },
  assignedUser: { fieldType: AdvanceFilterFieldType.STRING, operator: AdvanceFilterOperator.CONTAINS },
  ddiType: { fieldType: AdvanceFilterFieldType.STRING, operator: AdvanceFilterOperator.EQUALS },
};

export const DDI_DEFAULT_SEARCH_FIELD_NAMES = Object.keys(DDI_DEFAULT_FILTER_FIELD_CONFIG);

const EQUALS_ONLY = [AdvanceFilterOperator.EQUALS];

/** Advanced Search field catalog — Process Service interaction model, DDI field keys. */
export const ddiAdvanceFilterConfig: AdvanceFilterConfig[] = [
  {
    value: 'ddiSubtype',
    label: 'DDI Sub-type',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'SELECT',
    customFilterTypeOperator: EQUALS_ONLY,
  },
  {
    value: 'contactName',
    label: "Contact's name",
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
  {
    value: 'fileNumber',
    label: 'File number',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
  {
    value: 'lawyer',
    label: 'Lawyer',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
  {
    value: 'caseName',
    label: 'Case name',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
  {
    value: 'address',
    label: 'Address',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
  {
    value: 'city',
    label: 'City',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
  {
    value: 'state',
    label: 'State',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'SELECT',
    customFilterTypeOperator: EQUALS_ONLY,
  },
  {
    value: 'zipCode',
    label: 'Zip code',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
  {
    value: 'clientCode',
    label: 'Client code',
    fieldType: AdvanceFilterFieldType.ID,
    inputType: 'INPUT',
  },
  {
    value: 'courtName',
    label: 'Court name',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
  {
    value: 'createdAt',
    label: 'Date Created',
    fieldType: AdvanceFilterFieldType.DATE,
    inputType: 'DATE',
  },
  {
    value: 'dueDate',
    label: 'Due Date',
    fieldType: AdvanceFilterFieldType.DATE,
    inputType: 'DATE',
  },
  {
    value: 'caseType',
    label: 'Case Type',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'SELECT',
    customFilterTypeOperator: EQUALS_ONLY,
  },
  {
    value: 'caseSubType',
    label: 'Case Sub-type',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'SELECT',
    customFilterTypeOperator: EQUALS_ONLY,
  },
  {
    value: 'personForInquiry',
    label: 'Person for inquiry',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
  {
    value: 'entityForInquiry',
    label: 'Entity for inquiry',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
  {
    value: 'vin',
    label: 'Vehicle Identification Number (VIN)',
    fieldType: AdvanceFilterFieldType.STRING,
    inputType: 'INPUT',
  },
];

export const DDI_DATE_FILTER_FIELDS = new Set(
  ddiAdvanceFilterConfig.filter((c) => c.fieldType === AdvanceFilterFieldType.DATE).map((c) => c.value)
);

const uniqueAdvanceFieldValues = new Set(
  ddiAdvanceFilterConfig.filter((c) => !DDI_DATE_FILTER_FIELDS.has(c.value)).map((c) => c.value)
);

export const getDdiAllowedOperators = (config: AdvanceFilterConfig): AdvanceFilterOperator[] =>
  config.customFilterTypeOperator ?? FilterTypeOperatorMap[config.fieldType] ?? [];

export const canAddDdiAdvanceFilter = (selectedFields: (string | undefined)[]): boolean => {
  const usedUnique = new Set(
    selectedFields.filter((f): f is string => !!f && uniqueAdvanceFieldValues.has(f))
  );
  // DATE fields are never exhausted; allow add while any unique field remains unused OR any date exists
  return usedUnique.size < uniqueAdvanceFieldValues.size || DDI_DATE_FILTER_FIELDS.size > 0;
};

export const getAvailableDdiAdvanceFieldOptions = (
  selectedFilters: { id: string; filter?: AdvanceFilter }[],
  currentRowId: string
): AdvanceFilterConfig[] =>
  ddiAdvanceFilterConfig.filter((c) => {
    if (DDI_DATE_FILTER_FIELDS.has(c.value)) return true;
    return !selectedFilters.some(
      (sf) =>
        sf.id !== currentRowId &&
        sf.filter?.field === c.value &&
        uniqueAdvanceFieldValues.has(c.value)
    );
  });

const isValidDateValue = (value: string): boolean => {
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
};

export type DdiFilterRowValidationContext = {
  /** Allowed option values for SELECT/enum fields keyed by field name. */
  enumOptionsByField?: Record<string, string[]>;
};

export type DdiFilterRowErrors = {
  field?: string;
  operator?: string;
  value?: string;
  fieldType?: string;
};

export const validateDdiFilterRow = (
  filter: AdvanceFilter | undefined,
  ctx: DdiFilterRowValidationContext = {}
): DdiFilterRowErrors => {
  const errors: DdiFilterRowErrors = {};

  if (!filter?.field?.trim()) {
    errors.field = 'Field is required';
  }
  if (!filter?.operator) {
    errors.operator = 'Operator is required';
  }
  if (!filter?.fieldType) {
    errors.fieldType = 'Field type is required';
  }

  const rawValue = filter?.value;
  const trimmed = typeof rawValue === 'string' ? rawValue.trim() : '';
  if (!trimmed) {
    errors.value = 'Value is required';
  }

  if (filter?.field && filter.operator && filter.fieldType) {
    const config = ddiAdvanceFilterConfig.find((c) => c.value === filter.field);
    const defaultConfig = DDI_DEFAULT_FILTER_FIELD_CONFIG[filter.field];
    const allowed =
      (config ? getDdiAllowedOperators(config) : undefined) ??
      (defaultConfig ? [defaultConfig.operator] : FilterTypeOperatorMap[filter.fieldType]);

    if (allowed && !allowed.includes(filter.operator)) {
      errors.operator = 'Operator is not supported for this field';
    }

    if (config && filter.fieldType !== config.fieldType) {
      errors.fieldType = 'Field type does not match the selected field';
    }

    if (filter.fieldType === AdvanceFilterFieldType.DATE && trimmed && !isValidDateValue(trimmed)) {
      errors.value = 'Invalid date';
    }

    // Skip enum checks while lookups are still loading (empty arrays are not authoritative).
    const enumValues = ctx.enumOptionsByField?.[filter.field];
    if (enumValues && enumValues.length > 0 && trimmed && !enumValues.includes(trimmed)) {
      errors.value = 'Invalid enum value';
    }
  }

  return errors;
};

export const ddiFilterRowHasErrors = (errors: DdiFilterRowErrors): boolean =>
  Object.keys(errors).length > 0;

/** Zod schema for a fully populated filter row (payload shape). */
export const ddiFilterRowSchema = z
  .object({
    field: z.string().trim().min(1, 'Field is required'),
    operator: z.nativeEnum(AdvanceFilterOperator),
    value: z.union([z.string(), z.number()]).transform((v) => String(v).trim()),
    fieldType: z.nativeEnum(AdvanceFilterFieldType),
  })
  .superRefine((row, ctx) => {
    if (!row.value) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Value is required', path: ['value'] });
    }
    const config = ddiAdvanceFilterConfig.find((c) => c.value === row.field);
    const allowed = config
      ? getDdiAllowedOperators(config)
      : FilterTypeOperatorMap[row.fieldType];
    if (allowed && !allowed.includes(row.operator)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Operator is not supported for this field',
        path: ['operator'],
      });
    }
    if (row.fieldType === AdvanceFilterFieldType.DATE && row.value && !isValidDateValue(row.value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid date', path: ['value'] });
    }
  });

/** Build a filter entry from a default list-view search field value. */
export const buildDdiDefaultFilter = (field: string, value: string): AdvanceFilter | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const config = DDI_DEFAULT_FILTER_FIELD_CONFIG[field];
  if (!config) return null;
  return {
    field,
    value: trimmed,
    operator: config.operator,
    fieldType: config.fieldType,
  };
};

/**
 * Merge default list-view search values with advanced filters into a getAll `filters` array (AND).
 * Default-field entries in `existingFilters` are replaced from `defaultSearch`; other filters are kept.
 */
export const buildDdiSearchFilters = (
  defaultSearch: Record<string, string | undefined | null>,
  existingFilters: AdvanceFilter[],
  defaultFieldNames: string[] = DDI_DEFAULT_SEARCH_FIELD_NAMES
): AdvanceFilter[] => {
  const advanced = existingFilters.filter((f) => f.field && !defaultFieldNames.includes(f.field));
  const fromDefaults: AdvanceFilter[] = [];

  for (const [field, value] of Object.entries(defaultSearch)) {
    if (typeof value !== 'string') continue;
    const entry = buildDdiDefaultFilter(field, value);
    if (entry) fromDefaults.push(entry);
  }

  return [...advanced, ...fromDefaults];
};

/** Normalize filters for the getAll request: trim values, drop incomplete rows. */
export const normalizeDdiFiltersForRequest = (filters: AdvanceFilter[]): AdvanceFilter[] =>
  filters
    .map((f) => ({
      ...f,
      value: typeof f.value === 'string' ? f.value.trim() : f.value,
    }))
    .filter((f) => !!f.field && !!f.operator && !!f.fieldType && !!f.value);
